import {
    useCallback,
    useContext,
    useMemo
} from "react";
import {
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    SelectBox,
    Avatar
} from "ncore-ui-kit";
import type {
    ISelectBoxProps
} from "ncore-ui-kit";
import {
    AuthContext
} from "../../contexts/AuthContext";

interface Props {
    spreadBehaviour?: ISelectBoxProps<Record<string, unknown>>["spreadBehaviour"];
}

const OrganizationSwitcher = ({
    spreadBehaviour = "free"
}: Props) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        changeActiveOrganization,
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    if (user?.role === "root") {
        return null;
    }

    const orgData = user?.organizations || [];

    const mappedData = useMemo(() => {
        return orgData.map(org => ({
            title: org.name,
            id: org.id
        }));
    }, [
        orgData
    ]);

    const renderPrefix = useCallback(() => {
        if (!activeOrganizationId) {
            return null;
        }

        const activeOrg = orgData.find(org => org.id === activeOrganizationId);

        if (!activeOrg) {
            return null;
        }

        return <Avatar
            imageUrl={activeOrg.profilePicturePath || ""}
            title={activeOrg.name}
            size="xxSmall"
        />;
    }, [
        activeOrganizationId,
        orgData
    ]);

    const initialSelectedItems = useMemo(() => {
        return mappedData
            .filter(item => item.id === activeOrganizationId)
            .map(item => ({
                ...item,
                __title: item.title,
                __key: item.id
            }));
    }, [
        activeOrganizationId,
        mappedData
    ]);

    return <View
        style={stylesheet.organizationSwitcherContainer}
    >
        <SelectBox
            onChange={(selectedItems) => {
                if(!selectedItems || selectedItems.length === 0) {
                    if (activeOrganizationId !== null) {
                        changeActiveOrganization(null);
                    }
                    return;
                }

                const item = selectedItems[0] as unknown as typeof mappedData[0] & {
                    __key?: string;
                };

                if(!item) {
                    return;
                }

                const newId = item.__key || item.id;
                if (newId === activeOrganizationId) {
                    return;
                }

                changeActiveOrganization(newId);
            }}
            placeholder={localize("org-selector-placeholder")}
            initialSelectedItems={initialSelectedItems}
            titleExtractor={(item) => item.title}
            CustomPrefixComponent={renderPrefix}
            spreadBehaviour={spreadBehaviour}
            keyExtractor={(item) => item.id}
            style={stylesheet.selectBox}
            data={mappedData}
            minChoice={1}
        />
    </View>;
};
export default OrganizationSwitcher;
